import { answerBoy, boysConfig, checkAnswer, checkSend, latched, rideStopped, sendBoys, stopAreaBoys, tickAreaBoys, } from './areaboys.js';
import { jailed, locked } from './arrest.js';
import { createHide, cutClock, finalStretch, HIDE_DEADLINE, hideConfig, mayEscape, seeking, spendEscape, tickHide, } from './hide.js';
import { BOOST_FACTOR, boosted, createPowers, stopPowers, tickPowers, WIPER_FACTOR, wiping } from './powers.js';
import { checkAbility, conductorEnters, conductorMayVent, hiddenRefusal, onKnocked, resumeRoles, shadowed, shielded, stopRoles, tickRoles, useAbility, } from './roles.js';
const inOrder = (s) => s.order.map((id) => s.players[id]);
/** The classic park: drivers, one or two agberos, horns and breakdowns, votes. */
export const classicRules = {
    mode: 'classic',
    rides: true,
    onMatchStart() { },
    onTick() { },
    onMeeting() { },
    canCallMeeting: () => null,
    canVote: () => true,
    speed: (_s, _p, base) => base,
    visionFactor: () => null,
    refuse: () => null,
    mayVent: (_s, p) => (p.role === 'impostor' ? null : 'NOT_IMPOSTOR'),
    onVentEnter() { },
    onTaskDone() { },
    onDeadline: () => null,
    roleOpen: () => false,
    onMeetingEnd() { },
    mayKnock: () => null,
    onKnocked() { },
    hiddenFrom: () => false,
    checkAbility: () => 'NO_ABILITY',
    useAbility() { },
    onRideStop() { },
    checkBoys: () => 'NOT_IN_MODE',
    useBoys() { },
    checkWin(s) {
        const ps = inOrder(s);
        const aliveImp = ps.filter((p) => p.alive && p.role === 'impostor').length;
        // a driver locked in the yard is off the road: parity counts the free ones
        const aliveCrew = ps.filter((p) => p.alive && p.role === 'crew' && !jailed(s, p.id)).length;
        if (aliveImp === 0)
            return { team: 'crew', reason: 'votes' };
        // a critical wahala that ran out ends the round from its deadline (sabotage.ts), not here
        if (aliveImp >= aliveCrew)
            return { team: 'impostor', reason: 'parity' };
        if (s.taskProgress >= 1)
            return { team: 'crew', reason: 'tasks' };
        const crew = ps.filter((p) => p.role === 'crew');
        if (crew.length > 0 && crew.every((p) => p.departed))
            return { team: 'impostor', reason: 'forfeit' };
        return null;
    },
};
/** Power-up parks (GP-24): classic, plus pickups on the road, a speed boost and a wiper. */
export const powersRules = {
    ...classicRules,
    mode: 'powers',
    onMatchStart(s) {
        const powers = createPowers(s);
        if (powers)
            s.powers = powers;
    },
    onTick: (s, events) => tickPowers(s, events),
    onMeeting: (s) => stopPowers(s),
    speed: (s, p, base) => (boosted(s, p.id) ? Math.round(base * BOOST_FACTOR) : base),
    // the wiper clears NEPA's darkness around you, and then some
    visionFactor: (s, viewer) => (wiping(s, viewer.id) ? WIPER_FACTOR : null),
};
/** What hide and seek does not have: the horn, breakdown reports, votes, wahala and powers. */
const NOT_IN_HIDE = new Set([
    'player.report',
    'player.button',
    'meeting.vote',
    'sabotage.start',
    'sabotage.fix',
    'power.use',
    'ride.stop',
]);
/**
 * Hide and seek (GP-16b): the agbero is known and hunts; drivers hide until the clock runs out.
 * Stops cut the clock, drivers get a few shortcut escapes, and the final stretch favours the agbero.
 */
export const hideRules = {
    ...classicRules,
    mode: 'hide',
    // a hunt has no time for fares
    rides: false,
    onMatchStart(s) {
        s.hide = createHide(s);
        s.deadlines[HIDE_DEADLINE] = s.hide.endsAt;
    },
    onTick: (s, events) => tickHide(s, events),
    canCallMeeting: () => 'NOT_IN_MODE',
    canVote: () => false,
    refuse: (_s, _p, t) => (NOT_IN_HIDE.has(t) ? 'NOT_IN_MODE' : null),
    // the agbero stands still for the head start, then drives faster in the final stretch
    speed(s, p, base) {
        if (p.role !== 'impostor')
            return base;
        if (!seeking(s))
            return 0;
        return finalStretch(s) ? Math.round(base * hideConfig(s).finalSpeed) : base;
    },
    visionFactor: (s, viewer) => (viewer.role === 'impostor' && finalStretch(s) ? hideConfig(s).finalVision : null),
    mayVent: (s, p, action) => mayEscape(s, p, action),
    onVentEnter: (s, p) => spendEscape(s, p),
    onTaskDone: (s, p) => cutClock(s, p),
    onDeadline: (_s, id) => (id === HIDE_DEADLINE ? { team: 'crew', reason: 'survived' } : null),
    // the whole park knows who the agbero is; nobody learns anything else
    roleOpen: (_s, p) => p.role === 'impostor',
    checkWin(s) {
        const ps = inOrder(s);
        const crew = ps.filter((p) => p.role === 'crew');
        if (!ps.some((p) => p.alive && p.role === 'impostor'))
            return { team: 'crew', reason: 'forfeit' };
        if (crew.length > 0 && crew.every((p) => p.departed))
            return { team: 'impostor', reason: 'forfeit' };
        if (!crew.some((p) => p.alive))
            return { team: 'impostor', reason: 'caught' };
        return null;
    },
};
/**
 * Special roles (GP-17): a layer on a mode's rule set, only for a match that dealt roles. Each hook
 * runs the mode's own first, then the roles' part; with no roles the plain rule set plays.
 */
export function withRoles(base) {
    return {
        ...base,
        onTick(s, events) {
            base.onTick(s, events);
            tickRoles(s, events);
        },
        onMeeting(s) {
            base.onMeeting(s);
            stopRoles(s);
        },
        onMeetingEnd(s) {
            base.onMeetingEnd(s);
            resumeRoles(s);
        },
        refuse: (s, p, t) => base.refuse(s, p, t) ?? hiddenRefusal(s, p, t),
        // the conductor rides the shortcuts too, a few times a round
        mayVent: (s, p, action) => {
            const r = base.mayVent(s, p, action);
            return r === null ? null : r === 'NOT_IMPOSTOR' ? conductorMayVent(s, p, action) : r;
        },
        onVentEnter(s, p) {
            base.onVentEnter(s, p);
            conductorEnters(s, p);
        },
        // an area boy's shield turns a knock away
        mayKnock: (s, a, t) => base.mayKnock(s, a, t) ?? (shielded(s, t.id) ? 'PROTECTED' : null),
        onKnocked(s, t, events) {
            base.onKnocked(s, t, events);
            onKnocked(s, t, events);
        },
        // a shadow is out of every living driver's sight; agberos and spirits still see it
        hiddenFrom: (s, viewer, p) => base.hiddenFrom(s, viewer, p) || (viewer.role !== 'impostor' && shadowed(s, p)),
        checkAbility: (s, p, targetId) => checkAbility(s, p, targetId),
        useAbility: (s, p, targetId, events) => useAbility(s, p, targetId, events),
    };
}
/**
 * Area boys and the purse: a layer on a mode's rule set, only for a match that put them out. The
 * mode's own hooks run first; a keke a boy is blocking stands still.
 */
export function withAreaBoys(base) {
    return {
        ...base,
        onTick(s, events) {
            base.onTick(s, events);
            // an arrested or locked-up driver is off the road for the agberos too
            tickAreaBoys(s, events, (p) => !locked(s, p.id));
        },
        onMeeting(s) {
            base.onMeeting(s);
            stopAreaBoys(s);
        },
        // an agbero hanging off the keke: it crawls
        speed: (s, p, v) => {
            const sp = base.speed(s, p, v);
            return latched(s, p.id) ? Math.round(sp * boysConfig(s).latchSpeed) : sp;
        },
        onRideStop(s, p, events) {
            base.onRideStop(s, p, events);
            rideStopped(s, p, events);
        },
        checkBoys: (s, p, cmd) => (cmd.t === 'boys.answer' ? checkAnswer(s, p) : checkSend(s, p, cmd.targetId)),
        useBoys: (s, p, cmd, events) => cmd.t === 'boys.answer' ? answerBoy(s, p, cmd.choice, events) : sendBoys(s, p, cmd.targetId, events),
    };
}
/**
 * The modes' rule sets. Classic, Powers and Hide and seek are always here; Race registers itself
 * when `@wahala/simulation/race` is imported (the worker does; a phone loads it with the race road),
 * so a phone that never races never carries it.
 */
const RULES = {
    classic: classicRules,
    powers: powersRules,
    hide: hideRules,
};
/** Each mode with a layer on top (built from RULES, so a new mode gets its layers for free). */
const layered = (f) => Object.fromEntries(Object.entries(RULES).map(([k, r]) => [k, f(r)]));
const WITH_ROLES = layered(withRoles);
const WITH_BOYS = layered(withAreaBoys);
const WITH_BOTH = layered((r) => withRoles(withAreaBoys(r)));
/** A mode's rule set joins the table (and its layers); a mode module calls this once when loaded. */
export function registerRules(mode, rules) {
    RULES[mode] = rules;
    WITH_ROLES[mode] = withRoles(rules);
    WITH_BOYS[mode] = withAreaBoys(rules);
    WITH_BOTH[mode] = withRoles(withAreaBoys(rules));
}
export function rulesFor(s) {
    const table = s.roles ? (s.areaBoys ? WITH_BOTH : WITH_ROLES) : s.areaBoys ? WITH_BOYS : RULES;
    const rules = table[s.config.mode];
    // never another mode's rules in its place: a race played by the Park's rules would be a quiet bug
    // (a race's are loaded with @wahala/simulation/race)
    if (!rules)
        throw new Error(`no ${s.config.mode} rules`);
    return rules;
}
//# sourceMappingURL=rules.js.map